import { enumOptionsToObject } from '../../internal/enum-helpers'

export const PolarisAccount = 'Polaris'
export const StripeAccount = 'Stripe'
export const AccountTypeOptions = [
  'StripePaymentMethod',
  'StripePaymentIntent',
  'Customer',
  'Discount',
  'OrderLine',
  'Subscription',
  'Polaris',
  'Outfitter',
  'OutfitterGiftCardLiability',
  'StripeConnectAccount',
  'BankAccount',
  'Card',
  'Stripe',
  'StripeCharge',
  'StripePayout',
  'GiftCard',
  'RequestedRefundAccount'
] as const
export const AccountTypes = enumOptionsToObject(AccountTypeOptions)
export type AccountType = (typeof AccountTypeOptions)[number]

export function getOutfitterGiftCardAccount(outfitterShortId: string) {
  return `${outfitterShortId}_GIFT_CARDS`
}

export function getAccountType(account: string): AccountType {
  if (account === PolarisAccount) {
    // Money going to Polaris for platform fees, etc.
    return 'Polaris'
  } else if (account === StripeAccount) {
    // Money flowing to Stripe as a business due to platform fees, etc.
    return 'Stripe'
  } else if (account.startsWith('acct_')) {
    // Money flowing to a Stripe connect account
    return 'StripeConnectAccount'
  } else if (account.startsWith('sub_')) {
    // Money flowing to a specific subscription
    return 'Subscription'
  } else if (account.startsWith('pm_') || account.startsWith('src_')) {
    // Money flowing from a payment method (like a credit card)
    return 'StripePaymentMethod'
  } else if (account.startsWith('pi_')) {
    // Used just in the card authorization scenario - in that case we move money from a payment method to
    // a payment intent, ready to potentially be used later ("captured") or cancelled.
    return 'StripePaymentIntent'
  } else if (account.startsWith('ba_')) {
    // Money flowing to/from a specific bank account (likely belonging to an outfitter)
    return 'BankAccount'
  } else if (account.startsWith('card_')) {
    // Money flowing to/from a debit card (likely belonging to an outfitter)
    return 'Card'
  } else if (account.startsWith('O-') && !account.endsWith('_GIFT_CARDS')) {
    // Money flowing to/from an outfitter (usually before involving their Stripe connect account)
    return 'Outfitter'
  } else if (account.startsWith('O-') && account.endsWith('_GIFT_CARDS')) {
    // Money flowing to/from an outfitter (usually before involving their Stripe connect account)
    return 'OutfitterGiftCardLiability'
  } else if (account.startsWith('R-') || account.startsWith('AO-')) {
    return 'OrderLine'
  } else if (account.startsWith('C-')) {
    // Money flowing to/from customer - e.g. from payment method to the customer so they have
    // purchasing power within the transaction, or from customer to a line item
    return 'Customer'
  } else if (account.startsWith('DIS-')) {
    // Anything associated with a promotion or ad-hoc discount
    return 'Discount'
  } else if (account.startsWith('ch_') || account.startsWith('py_')) {
    return 'StripeCharge'
  } else if (account.startsWith('po_')) {
    return 'StripePayout'
  } else if (account.startsWith('GIFT-')) {
    return 'GiftCard'
  } else if (account.startsWith('REQUESTED-REFUNDS-')) {
    return 'RequestedRefundAccount'
  } else {
    throw new Error(`Unrecognized account: ${account}`)
  }
}
